import { toast } from "sonner";
import { useContext } from "react";
import { CrmProvider, AuthMethod, CrmFormData } from "../types";
import AuthMethodSelector from "../AuthMethodSelector";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

type HubspotAuthConfigurationProps = {
  formData: CrmFormData;
  onFormDataChange: (data: Partial<CrmFormData>) => void;
  hasConnection: boolean;
  crmConnection: any;
};

const HubspotAuthConfiguration = ({
  formData,
  onFormDataChange,
  hasConnection,
  crmConnection,
}: HubspotAuthConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const utils = trpc.useUtils();
  const createConnection = trpc.crmSelfConfig.create.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al crear la conexión.",
      });
    },
  });

  const testConnection = trpc.crmSelfConfig.testConnection.useMutation({
    onSuccess: async (result) => {
      if (result.success) {
        const accessToken = formData.hubspotAccessToken || crmConnection?.hubspot?.accessToken;

        if (merchantId && accessToken) {
          await createConnection.mutateAsync({
            merchantId,
            provider: "hubspot" as CrmProvider,
            hubspot: {
              accessToken: accessToken || "",
            },
            status: "success",
            enabled: true,
          });
        }

        toast.success("Conexión exitosa", {
          description: "La conexión con HubSpot se ha establecido correctamente.",
        });
      } else {
        toast.error("Error de conexión", {
          description: result.error || "No se pudo establecer la conexión con HubSpot.",
        });
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al probar la conexión.",
      });
    },
  });

  const handleTestConnection = () => {
    const accessToken = (
      formData.hubspotAccessToken ||
      crmConnection?.hubspot?.accessToken ||
      ""
    ).trim();

    if (!accessToken) {
      toast.warning("Token requerido", {
        description: "Por favor ingresa el token de la Private App antes de probar la conexión.",
      });
      return;
    }

    // HubSpot's token travels in the generic `apiKey` field, like every other
    // provider's single-credential test.
    testConnection.mutate({
      provider: "hubspot" as CrmProvider,
      apiKey: accessToken,
    });
  };

  const handleAuthMethodChange = (method: AuthMethod) => {
    onFormDataChange({
      authMethod: method,
      hubspotAccessToken:
        formData.authMethod === method ? formData.hubspotAccessToken : "",
    });
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ hubspotAccessToken: e.target.value });
  };

  const isConnected = hasConnection && crmConnection.status === "success";

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-lg font-bold text-gray-700">Método de autenticación</Label>
            {isConnected && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Conexión exitosa
              </span>
            )}
          </div>
          <AuthMethodSelector
            methods={["apiKey"]}
            selectedMethod={isConnected ? "apiKey" : formData.authMethod || "apiKey"}
            onSelect={handleAuthMethodChange}
            isDisabled={isConnected}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Token de Private App
          </Label>
          <Input
            type="password"
            value={
              isConnected && crmConnection.hubspot?.accessToken
                ? "••••••••••••"
                : formData.hubspotAccessToken || crmConnection?.hubspot?.accessToken || ""
            }
            onChange={handleTokenChange}
            placeholder="Ingresa el token de tu Private App de HubSpot"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={isConnected}
          />
          <p className="mt-1 text-xs text-gray-500">
            Crea una Private App en HubSpot (Configuración → Integraciones → Private Apps) con los
            permisos de CRM (contactos y negocios) y pega aquí su token de acceso.
          </p>
        </div>

        {!isConnected && (
          <Button
            variant="main"
            onClick={handleTestConnection}
            disabled={
              testConnection.isLoading ||
              (!formData.hubspotAccessToken && !crmConnection?.hubspot?.accessToken)
            }
          >
            {testConnection.isLoading && <Loader2 className="size-4 animate-spin" />}
            Probar conexión
          </Button>
        )}
      </div>
    </div>
  );
};

export default HubspotAuthConfiguration;
